import {
  Box,
  Card,
  Container,
  Grid,
  Typography,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
  Chip,
  Button,
  CircularProgress,
} from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import { FaAngleLeft } from 'react-icons/fa6';
import { FaAngleRight } from 'react-icons/fa6';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import { useEffect, useRef, useState } from 'react';
import Page from '../../components/Page';
import { IoMdPhotos } from 'react-icons/io';
import FullscreenImageViewer from './FullscreenImageViewer';
import { useAppDispatch } from '../../store';
import { useNavigate, useParams } from 'react-router-dom';
import useToggle from '../../hooks/useToggle';
import KeyFeatureDailog from './Dailogs/KeyFeatureDailog';
import { MdMotionPhotosOn } from 'react-icons/md';
import { decryptString, getAssetURL } from '../../utils/url';
import { parseCurrencyString } from '../../utils/formateCarPrice';
import { CarAvailability } from '../../enum';
import { getCar } from '../../store/cars/api';

const Index: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const infoId = decryptString(params.id ?? '');
  const scrollRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const [info, setInfo] = useState<any>(null);
  const [carPhoto, setCarPhoto] = useState<any>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [isFail, setIsFail] = useState<boolean>(false);
  const [fullscreenIndex, setFullscreenIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (info?.car?.photos && info.car.photos?.length > 0) {
      setCarPhoto(info.car.photos);
    }
  }, [info]);

  const {
    toggle: keyFeaturesToggle,
    onOpen: keyFeaturesOnOpen,
    onClose: keyFeaturesOnClose,
  } = useToggle();

  const carInfo = [
    { label: 'Exterior', value: info?.car?.exterior },
    { label: 'Interior', value: info?.car?.interior },
    { label: 'Body Type', value: info?.car?.bodyType },
    { label: 'Drive Type', value: info?.car?.driveType },
    { label: 'Engine', value: info?.car?.engine },
    { label: 'Model Code', value: info?.car?.modelCode },
    { label: 'Mileage', value: info?.car?.mileage },
    { label: 'Car Type', value: info?.car?.carType },
    { label: 'Dealer Name', value: info?.car?.dealerName },
    { label: 'VIN Number', value: info?.car?.vinNumber },
    { label: 'Stock Number', value: info?.car?.stockNumber },
  ];

  const description = info?.car?.description || '';
  const words = description.trim() ? description.split(/\s+/) : [];
  const isLong = words.length > 50;
  const displayedText = expanded ? description : words.slice(0, 50).join(' ');
  const photoItems =
    carPhoto?.map((photo: any) => getAssetURL(photo.key, photo?.url)) || [];
  const parsedPrice = parseCurrencyString(info?.car?.price);
  let imagesPerView = 1;

  if (isSm) {
    imagesPerView = 2;
  } else if (isMd) {
    imagesPerView = 3;
  } else if (isLgUp) {
    imagesPerView = 4;
  }

  const scrollLeft = () => {
    let newIndex = currentIndex - 1;
    if (newIndex < 0) newIndex = carPhoto?.length - 1;
    setCurrentIndex(newIndex);
    scrollToIndex(newIndex);
  };

  const scrollRight = () => {
    let newIndex = currentIndex + 1;
    if (newIndex >= carPhoto?.length) newIndex = 0;
    setCurrentIndex(newIndex);
    scrollToIndex(newIndex);
  };

  const scrollToIndex = (index: number) => {
    const el = scrollRef.current;
    if (!el) return;

    const childWidth =
      el.firstChild instanceof HTMLElement
        ? el.firstChild.offsetWidth + 16
        : 300;

    el.scrollTo({ left: index * childWidth, behavior: 'smooth' });
  };

  const openFullscreen = (index: number) => {
    setFullscreenIndex(index);
    setFullscreenOpen(true);
  };

  useEffect(() => {
    scrollToIndex(currentIndex);
  }, [currentIndex]);

  useEffect(() => {
    if (!infoId) return;
    const fetchCar = async () => {
      setLoading(true);

      try {
        const result = await dispatch(getCar({ id: infoId })).unwrap();
        if (result) {
          setInfo({
            car: { ...result },
          });
        } else {
          setIsFail(true);
        }
      } catch (error) {
        // Error handled silently
      } finally {
        setLoading(false);
      }
    };
    fetchCar();
  }, [dispatch, infoId]);

  return (
    <Page title={'Info'}>
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '80vh',
          }}
        >
          <CircularProgress />
        </Box>
      ) : isFail || !info ? (
        <Box
          display={'flex'}
          height={'80vh'}
          alignItems={'center'}
          justifyContent={'center'}
        >
          <Typography
            variant='h5'
            fontWeight={600}
          >
            Car not found. Please try again later.
          </Typography>
        </Box>
      ) : (
        <Container
          maxWidth='xl'
          sx={{ p: 0, pl: { xs: 0, sm: 2 }, pr: { xs: 0, sm: 2 }, pb: 2 }}
        >
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            mt={2}
            mb={4}
            sx={{
              display: {
                xs: 'none',
                sm: 'flex',
              },
            }}
          >
            <Typography
              variant='h5'
              fontWeight={600}
            >
              {info?.car?.title}
            </Typography>

            <Box
              display='flex'
              gap={2}
            >
              <Button
                variant='outlined'
                color='primary'
                onClick={() => navigate(-1)}
              >
                Back
              </Button>
            </Box>
          </Box>
          <Grid
            container
            display={{ xs: 'block', sm: 'none' }}
            sx={{
              minHeight: info?.car?.dealerId ? 360 : 260,
              // backgroundImage: 'url(/static/mobile-nav.svg)',
              // backgroundRepeat: 'no-repeat',
              // backgroundSize: 'cover',
              // backgroundPosition: 'center',
              bgcolor: '#000000',
              position: 'relative',
            }}
          >
            {/* Profile Section */}
            {
              <Box
                sx={{
                  px: 2.5,
                  pt: 3,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    component='img'
                    src='/static/inventory_page/backWhite.svg'
                    alt='Scan'
                    sx={{ width: 40, height: 40, mr: 1 }}
                    onClick={() => navigate(-1)}
                  />
                  <Typography
                    sx={{
                      fontSize: '20px',
                      fontWeight: 600,
                      color: '#fff',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {info?.car?.title?.length > 20
                      ? `${info.car.title.substring(0, 20)}...`
                      : info?.car?.title}
                  </Typography>
                </Box>
              </Box>
            }
            {/* Images Section - Mobile */}
            <Box
              sx={{
                mt: 2,
                display: { xs: 'block', sm: 'none' },
                position: 'relative',
                width: '100%',
                overflow: 'hidden',
                textAlign: 'center',
              }}
            >
              {carPhoto?.length > 0 && (
                <>
                  {/* Image */}
                  <Box
                    key={currentIndex}
                    sx={{
                      opacity:
                        info?.car?.availability !== CarAvailability.available
                          ? 0.5
                          : 1,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      height: 220,
                      transition: 'transform 0.5s ease',
                    }}
                  >
                    <Box
                      component='img'
                      src={getAssetURL(
                        carPhoto[currentIndex]?.key,
                        carPhoto[currentIndex]?.url
                      )}
                      alt={`Car ${currentIndex + 1}`}
                      sx={{
                        width: '85%', // centered image width
                        height: '200px',
                        objectFit: 'contain',
                      }}
                      onClick={() => openFullscreen(currentIndex)}
                    />
                  </Box>

                  {/* Left Button */}
                  {carPhoto?.length > 1 && (
                    <>
                      <IconButton
                        onClick={scrollLeft}
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: 10,
                          transform: 'translateY(-50%)',
                          zIndex: 10,
                          backgroundColor: 'rgba(255, 255, 255, 0.486)',
                          boxShadow: 2,
                          '&:hover': {
                            backgroundColor: '#722410',
                          },
                        }}
                      >
                        <FaAngleLeft color='#ffffff' />
                      </IconButton>

                      {/* Right Button */}
                      <IconButton
                        onClick={scrollRight}
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          right: 10,
                          transform: 'translateY(-50%)',
                          zIndex: 10,
                          backgroundColor: 'rgba(255,255,255,0.486)',
                          boxShadow: 2,
                          '&:hover': {
                            backgroundColor: '#722410',
                          },
                        }}
                      >
                        <FaAngleRight color='#ffffff' />
                      </IconButton>
                    </>
                  )}

                  {/* Sold Out Overlay */}
                  {info?.car?.availability !== CarAvailability.available && (
                    <Box
                      component='img'
                      src='/static/inventory_page/sold_out.png'
                      alt='Sold Out'
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        maxWidth: '80%',
                        maxHeight: '80%',
                        pointerEvents: 'none',
                        userSelect: 'none',
                      }}
                    />
                  )}
                </>
              )}
            </Box>

            {/* Overlapping Profile Card */}
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                bottom: info?.car?.dealerId ? -50 : -36,
                transform: 'translateX(-50%)',
                width: '90%',
                zIndex: 2,
              }}
            >
              <Card
                sx={{
                  borderRadius: 1,
                  boxShadow: 4,
                  overflow: 'hidden',
                  background: 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 1.5,
                  pb: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>
                      <Typography
                        fontSize='16px'
                        fontWeight={600}
                      >
                        {info?.car?.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '18px',
                          fontWeight: 600,
                          background: (theme: any) =>
                            theme.palette.gradients.primary,
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          color: 'transparent',
                        }}
                      >
                        {parsedPrice?.amount !== '-'
                          ? `${parsedPrice?.symbolOrCode}${parsedPrice?.amount}`
                          : '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Box>
          </Grid>
          {/* Mobile View */}
          <Grid
            container
            display={{ xs: 'block', sm: 'none' }}
          >
            <Grid
              size={{ xs: 12 }}
              p={2}
              mt={info?.car?.dealerId ? 10 : 4}
            >
              <Box>
                <Typography
                  fontSize='16px'
                  fontWeight={600}
                >
                  Description:
                </Typography>
                <Typography
                  fontSize='14px'
                  color='text.secondary'
                  sx={{ display: 'inline' }}
                >
                  {displayedText}
                  {isLong && (
                    <Box
                      component='span'
                      onClick={() => setExpanded(!expanded)}
                      sx={{
                        cursor: 'pointer',
                        verticalAlign: 'middle',
                        ml: 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        color: 'primary.light',
                        userSelect: 'none',
                      }}
                    >
                      {expanded ? `Show less` : `Show more`}
                    </Box>
                  )}
                </Typography>
              </Box>
              <Divider sx={{ mt: 2 }} />
              <Grid
                size={{ xs: 12, sm: 6 }}
                p={1}
              >
                {Array.from({ length: Math.ceil(carInfo.length / 2) }).map(
                  (_, i) => {
                    const pair = carInfo.slice(i * 2, i * 2 + 2);
                    return (
                      <Box
                        key={i}
                        mb={2}
                      >
                        <Grid
                          container
                          spacing={2}
                        >
                          {pair.map(({ label, value }) => (
                            <Grid
                              size={{ xs: 6 }}
                              key={label}
                            >
                              <Typography
                                sx={{
                                  fontSize: '14px',
                                  fontWeight: 500,
                                  color: 'text.secondary',
                                }}
                              >
                                {label}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '14px',
                                  color: '#000',
                                  display: 'flex',
                                  alignItems: 'center',
                                  wordBreak: 'break-all',
                                }}
                              >
                                {label === 'Car Type' &&
                                (value?.toString().toLowerCase() === 'used' ||
                                  value?.toString().toLowerCase() === 'new') ? (
                                  <Chip
                                    label={
                                      value?.toString().toLowerCase() === 'used'
                                        ? 'Used'
                                        : 'New'
                                    }
                                    size='medium'
                                    sx={{
                                      mt: 0.5,
                                      backgroundColor:
                                        value === 'Used'
                                          ? '#FFF5E7'
                                          : '#DFFFE3',
                                      color:
                                        value === 'Used'
                                          ? '#E08C0E'
                                          : '#2E7D32',
                                      fontWeight: 600,
                                      fontSize: 14,
                                      height: 25,
                                    }}
                                  />
                                ) : value ? (
                                  value
                                ) : (
                                  <Box
                                    component='span'
                                    sx={{ ml: 1 }}
                                  >
                                    -
                                  </Box>
                                )}
                              </Typography>
                            </Grid>
                          ))}
                        </Grid>

                        {/* Show divider only if the next pair has content */}
                        {(() => {
                          const nextPair = carInfo.slice(
                            (i + 1) * 2,
                            (i + 1) * 2 + 2
                          );
                          const hasNextContent = nextPair.some(
                            ({ value }) => value
                          );
                          return hasNextContent ? (
                            <Divider sx={{ mt: 2 }} />
                          ) : null;
                        })()}
                      </Box>
                    );
                  }
                )}
              </Grid>
              <Divider />
              <Grid
                size={{ xs: 12, sm: 6 }}
                p={1}
              >
                <Typography fontWeight={600}>Highlighted Features:</Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mt: 1,
                  }}
                >
                  {info?.car?.keyFeatures
                    ?.slice(0, 10)
                    ?.map((feature: string, index: number) => (
                      <Chip
                        key={index}
                        icon={
                          <MdMotionPhotosOn
                            size={16}
                            color='#414141'
                          />
                        }
                        label={feature}
                        size='medium'
                        sx={{
                          padding: '12px 2px',
                          backgroundColor: '#007CB41F',
                          color: '#93989F',
                          fontWeight: 400,
                          fontSize: '14px',
                        }}
                      />
                    ))}
                </Box>
                {info?.car?.keyFeatures?.length > 10 && (
                  <Box mt={1}>
                    <Typography
                      onClick={keyFeaturesOnOpen}
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: 'info.light',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }}
                    >
                      All {info.car.keyFeatures.length} Highlights
                    </Typography>
                  </Box>
                )}
              </Grid>
              <Divider />
              {info?.scanBy && (
                <Card
                  sx={{
                    borderRadius: '16px',
                    padding: 2,
                    backgroundColor: '#FFFFFF',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 0 15px rgba(0, 0, 0, 0.15)',
                    mt: 2,
                  }}
                >
                  <Typography
                    variant='h6'
                    sx={{ mb: 2 }}
                  >
                    User Details:
                  </Typography>

                  <Box>
                    <Typography variant='h6'>
                      {info?.scanBy?.firstName} {info?.scanBy?.lastName}
                    </Typography>
                    <Typography variant='body1'>
                      Email: {info?.scanBy?.email}
                    </Typography>
                    <Typography variant='body1'>
                      Mobile: {info?.scanBy?.countryCode}{' '}
                      {info?.scanBy?.mobileNumber}
                    </Typography>
                  </Box>
                </Card>
              )}
            </Grid>
          </Grid>
          {/* Desktop View */}
          <Grid
            container
            spacing={2}
            sx={{
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {/* CAROUSEL */}
            <Grid
              size={{ xs: 12, md: 12 }}
              sx={{
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {carPhoto?.length > 0 && (
                <Box
                  sx={{
                    position: 'relative',
                    opacity:
                      info?.car?.availability !== CarAvailability.available
                        ? 0.5
                        : 1,
                  }}
                >
                  {carPhoto?.length > imagesPerView && (
                    <>
                      <IconButton
                        onClick={scrollLeft}
                        sx={{
                          position: 'absolute',
                          left: 5,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 10,
                          backgroundColor: '#fff',
                          boxShadow: 1,
                          opacity: 1,
                          '&:hover': {
                            backgroundColor: '#eee',
                          },
                        }}
                      >
                        <ArrowBackIosNewRoundedIcon />
                      </IconButton>
                      <IconButton
                        onClick={scrollRight}
                        sx={{
                          position: 'absolute',
                          right: 5,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 10,
                          backgroundColor: '#fff',
                          boxShadow: 1,
                          opacity: 1,
                          '&:hover': {
                            backgroundColor: '#eee',
                          },
                        }}
                      >
                        <ArrowForwardIosRoundedIcon />
                      </IconButton>
                    </>
                  )}

                  {/* Scrollable Images */}
                  <Grid
                    // item
                    ref={scrollRef}
                    sx={{
                      display: 'flex',
                      // gap: 2,
                      borderRadius: '12px',
                      overflowX: 'auto',
                      scrollBehavior: 'smooth',
                      // paddingBottom: 2,
                      scrollbarWidth: 'none',
                      '&::-webkit-scrollbar': { display: 'none' },
                    }}
                  >
                    {carPhoto?.map((photo: any, index: number) => (
                      <Box
                        key={photo.id || index}
                        sx={{
                          minWidth: {
                            xs: '100%',
                            sm: '50%',
                            md: '33.33%',
                          },
                          flexShrink: 0,
                          overflow: 'hidden',
                          boxShadow: 1,
                          cursor: 'pointer',
                        }}
                        onClick={() => openFullscreen(index)}
                      >
                        <Box
                          component='img'
                          src={getAssetURL(photo?.key, photo?.url)}
                          alt={`Car ${index + 1}`}
                          sx={{
                            width: '100%',
                            height: 350,
                            objectFit: 'cover',
                          }}
                        />
                      </Box>
                    ))}
                  </Grid>

                  {/* Image Counter */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 12,
                      left: 12,
                      backgroundColor: 'rgba(255, 255, 255, 0.575)',
                      color: '#000',
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: 14,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      zIndex: 12,
                    }}
                  >
                    <IoMdPhotos
                      size={16}
                      style={{ marginRight: 4 }}
                    />
                    {Math.min(currentIndex + 1, carPhoto?.length)} of{' '}
                    {carPhoto?.length}
                  </Box>

                  {info?.car?.availability !== CarAvailability.available && (
                    <Box
                      component='img'
                      src='/static/inventory_page/sold_out.png'
                      alt='Sold Out'
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        maxWidth: '80%',
                        maxHeight: '80%',
                        pointerEvents: 'none',
                        userSelect: 'none',
                      }}
                    />
                  )}
                </Box>
              )}
              {/* TITLE & PRICE */}
              <Card
                sx={{
                  borderRadius: '16px',
                  padding: '12px',
                  backgroundColor: '#FFFFFF',
                  mt: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  boxShadow: '0 0 15px rgba(0, 0, 0, 0.15)',
                }}
              >
                <Box>
                  <Typography
                    fontSize='16px'
                    fontWeight={600}
                  >
                    {info?.car?.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '18px',
                      fontWeight: 600,
                      background: (theme: any) =>
                        theme.palette.gradients.primary,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      color: 'transparent',
                    }}
                  >
                    {parsedPrice?.amount !== '-'
                      ? `${parsedPrice?.symbolOrCode}${parsedPrice?.amount}`
                      : '-'}
                  </Typography>
                </Box>
              </Card>
            </Grid>
            <Grid
              size={{ xs: 12 }}
              sx={{
                display: { xs: 'none', sm: 'block' },
              }}
            >
              <Card
                sx={{
                  borderRadius: '16px',
                  padding: 2,
                  backgroundColor: '#FFFFFF',
                  display: 'flex',
                  justifyContent: 'space-between',
                  boxShadow: '0 0 15px rgba(0, 0, 0, 0.15)',
                  mt: 2,
                }}
              >
                <Grid container>
                  <Grid
                    size={{ xs: 12, sm: 6 }}
                    p={1}
                  >
                    <Box>
                      <Typography
                        fontSize='16px'
                        fontWeight={600}
                      >
                        Description
                      </Typography>
                      <Typography
                        fontSize='14px'
                        color='text.secondary'
                        sx={{ display: 'inline' }}
                      >
                        {displayedText}
                        {isLong && (
                          <Box
                            component='span'
                            onClick={() => setExpanded(!expanded)}
                            sx={{
                              cursor: 'pointer',
                              verticalAlign: 'middle',
                              ml: 1,
                              display: 'inline-flex',
                              alignItems: 'center',
                              color: 'primary.light',
                              userSelect: 'none',
                            }}
                          >
                            {expanded ? `'Show less` : `Show more`}
                          </Box>
                        )}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        fontWeight={600}
                        mt={2}
                      >
                        Highlighted Features:
                      </Typography>
                      <ul
                        style={{
                          paddingLeft: 0,
                          listStyle: 'none',
                          marginTop: 8,
                        }}
                      >
                        {info?.car?.keyFeatures
                          ?.slice(0, 10)
                          ?.map((feature: string, index: number) => (
                            <li
                              key={index}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                marginBottom: 6,
                              }}
                            >
                              <MdMotionPhotosOn
                                size={16}
                                color='#414141'
                                aria-hidden='true'
                              />
                              <Typography fontSize={14}>{feature}</Typography>
                            </li>
                          ))}
                      </ul>
                      {info?.car?.keyFeatures?.length > 10 && (
                        <Box mt={1}>
                          <Typography
                            onClick={keyFeaturesOnOpen}
                            sx={{
                              fontSize: '14px',
                              fontWeight: 500,
                              color: 'info.light',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                            }}
                          >
                            All {info.car.keyFeatures.length} Highlights
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  <Grid
                    size={{ xs: 12, sm: 6 }}
                    p={1}
                  >
                    {Array.from({ length: Math.ceil(carInfo.length / 2) }).map(
                      (_, i) => {
                        const pair = carInfo.slice(i * 2, i * 2 + 2);
                        return (
                          <Box
                            key={i}
                            mb={2}
                          >
                            <Grid
                              container
                              spacing={2}
                            >
                              {pair.map(({ label, value }) => (
                                <Grid
                                  size={{ xs: 6 }}
                                  key={label}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: '14px',
                                      fontWeight: 500,
                                      color: 'text.secondary',
                                    }}
                                  >
                                    {label}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: '14px',
                                      fontWeight: 600,
                                      color: '#000',
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    {label === 'Car Type' &&
                                    (value?.toString().toLowerCase() ===
                                      'used' ||
                                      value?.toString().toLowerCase() ===
                                        'new') ? (
                                      <Chip
                                        label={
                                          value?.toString().toLowerCase() ===
                                          'used'
                                            ? 'Used'
                                            : 'New'
                                        }
                                        size='medium'
                                        sx={{
                                          mt: 0.5,
                                          backgroundColor:
                                            value === 'Used'
                                              ? '#FFF5E7'
                                              : '#DFFFE3',
                                          color:
                                            value === 'Used'
                                              ? '#E08C0E'
                                              : '#2E7D32',
                                          fontWeight: 600,
                                          fontSize: 14,
                                          height: 25,
                                        }}
                                      />
                                    ) : value ? (
                                      value
                                    ) : (
                                      <Box
                                        component='span'
                                        sx={{ ml: 1 }}
                                      >
                                        -
                                      </Box>
                                    )}
                                  </Typography>
                                </Grid>
                              ))}
                            </Grid>

                            {/* Show divider only if the next pair has content */}
                            {(() => {
                              const nextPair = carInfo.slice(
                                (i + 1) * 2,
                                (i + 1) * 2 + 2
                              );
                              const hasNextContent = nextPair.some(
                                ({ value }) => value
                              );
                              return hasNextContent ? (
                                <Divider sx={{ mt: 2 }} />
                              ) : null;
                            })()}
                          </Box>
                        );
                      }
                    )}
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Container>
      )}
      {fullscreenOpen && (
        <FullscreenImageViewer
          images={photoItems}
          initialIndex={fullscreenIndex}
          onClose={() => setFullscreenOpen(false)}
        />
      )}
      <KeyFeatureDailog
        alertTemplateToggle={keyFeaturesToggle}
        alertTemplateOnClose={keyFeaturesOnClose}
        features={info?.car?.keyFeatures || []}
      />
    </Page>
  );
};

export default Index;
